// Framework
import { Component, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

// Providers
import { DataService } from '../../services/data.service';
import { AppService } from '../../services/app.service';
import { BsModalService } from 'ngx-bootstrap';

// Modals / Components
import { RegisterPaymentComponent } from '../register-payment/register-payment.component';
import { ContractTermsModalComponent } from '../contract-terms-modal/contract-terms-modal.component';

// NPM
import SignaturePad from 'signature_pad';
import * as moment from 'moment';

// Model
import { PaymentInfo } from '../../model/free-trial';

@Component({
    selector: 'sign-up-auth',
    templateUrl: './sign-up-auth.component.html'
})

export class SignUpAuthComponent {
    signaturePad: SignaturePad;
    expirationDate: any;
    isLoading: boolean = false;

    agreed: boolean = false;
    doneClicked: boolean = false;
    // Set once createQuickbooksInvoicePaymentAccount succeeds, so a retry after a later failure
    // (sendContract/movePotentialDaycareToActive) doesn't re-charge the card.
    paymentAlreadyProcessed: boolean = false;

    readonly standardTerms: string = "I understand I'm enrolling in a subscription program, and I authorize ChildPilot via electronic signature to charge my credit card account monthly for the price in effect each period until I cancel, beginning on the last day of my first billed month of use. I certify that the information above is correct and understand it will appear on my invoice."
    readonly txFccnNoPaymentTerms: string = "I understand this account's ChildPilot software subscription is provided at no cost for 12 months through ChildPilot's partnership with the Texas Family Child Care Network (TX FCCN), with no monthly subscription fee charged. I certify that the information above is correct."
    readonly txFccnMerchantTerms: string = "I understand this account's ChildPilot software subscription is provided at no cost for 12 months through ChildPilot's partnership with the Texas Family Child Care Network (TX FCCN) — no monthly subscription fee will be charged. By providing my card information, I authorize ChildPilot via electronic signature to process payment transactions through its merchant services solution at the disclosed rates (2.90% per credit card transaction, $0.95 per ACH transaction) for any payments I choose to run through ChildPilot. I certify that the information above is correct and understand it will appear on my invoice."

    get terms(): string {
        if (!this.isTxFccnFree()) return this.standardTerms;
        return this.skipPayment() ? this.txFccnNoPaymentTerms : this.txFccnMerchantTerms;
    }

    @ViewChild('RegisterPayment') RegisterPayment: RegisterPaymentComponent;
    constructor (
        private modalSvc: BsModalService,
        private router: Router,
        public dataSvc: DataService,
        private appSvc: AppService
    ) { }

    // Init Signature Pad
    ngOnInit() {
        if (!this.dataSvc.daycare)
            this.router.navigate(['sign-up'])

        var canvas = document.querySelector('canvas');
        this.signaturePad = new SignaturePad(canvas);
    }

    // Thin wrapper so the template (and this component's own methods) can keep calling
    // isTxFccnFree() directly — the actual check lives on DataService, shared with
    // contract-terms-modal, so both agree on what counts as a TX FCCN school.
    isTxFccnFree(): boolean {
        return this.dataSvc.isTxFccnFree();
    }

    // Nothing to charge today — skip card capture entirely (TX FCCN school not using ChildPilot's own
    // merchant processing). A TX FCCN school that *does* want merchant processing still needs a card on
    // file for those transaction fees, even though the software subscription itself is free.
    // Uses the school's real isUtilizingMerchant field, not fees.credit/fees.ach — TX FCCN schools
    // always disclose the standard 2.90%/$0.95 merchant rate in their contract regardless of whether
    // merchant processing is actually turned on, so a non-zero rate isn't a reliable "has merchant" signal.
    skipPayment(): boolean {
        return this.isTxFccnFree() && !this.dataSvc.daycare.isUtilizingMerchant;
    }

    async btnSignup() {
        try {
            this.doneClicked = true;
            this.isLoading = true;

            // Destructure daycare object for easy access
            const { alias, name, cpAccountRep } = this.dataSvc.daycare;

            let paymentInfo: PaymentInfo = null;
            if (!this.skipPayment()) {
                // Authorize payment info
                paymentInfo = await this.RegisterPayment.btnDone(alias);
                // If no payment info, assume there was an authorization/form issue
                if (!paymentInfo) {
                    this.isLoading = false;
                    return;
                }
            }

            // get signature as .png image
            let signatureImage: string = this.signaturePad.toDataURL();

            // upload signature to firebase storage & receive download URL
            this.dataSvc.addImageToStorage(signatureImage, alias).then(async (downloadURL: string) => {

                let promises: Promise<any>[] = [];

                if (!this.skipPayment() && !this.paymentAlreadyProcessed) {
                    // Register the card (and, for paid plans, invoice/charge the setup fee) — for a
                    // TX FCCN school with merchant processing on, amount/balance are $0 since there's
                    // no ChildPilot subscription fee, but the card still needs to be on file for
                    // future merchant transactions per the signed terms.
                    let amount = this.getFirstPaymentAmount();
                    let balance = this.dataSvc.daycare.fees.cpSetupFee.amount;
                    await this.dataSvc.createQuickbooksInvoicePaymentAccount(paymentInfo.cardNumber, paymentInfo.token, paymentInfo.expDate, paymentInfo.zip, name, amount, balance, this.dataSvc.daycare, this.dataSvc.signUpInfo.adminEmail, this.dataSvc.signUpInfo.adminName, cpAccountRep);
                    // Guards against double-charging: if sendContract/movePotentialDaycareToActive
                    // below fail and the customer retries, don't re-run the card charge that already
                    // succeeded — only the two calls below get retried.
                    this.paymentAlreadyProcessed = true;
                }

                promises.push(this.dataSvc.sendContract(downloadURL));
                promises.push(this.dataSvc.movePotentialDaycareToActive(name));

                Promise.all(promises).then(() => {
                    this.isLoading = false;
                    this.router.navigate(['success200']);

                    // delete daycare
                    this.dataSvc.deleteDaycare(name, true).catch((err) => {this.handleErr(err)});

                }).catch((err) => {
                    // Sending the contract or activating the account failed — don't tell the customer
                    // they're done when they aren't; keep them here and let them retry or contact support.
                    this.handleErr(err, "Something went wrong finishing your signup. Please try again, or contact ChildPilot support if this keeps happening.");
                })

            }).catch((err) => {
                this.handleErr(err);
            });
        }
        catch(e) {
            this.handleErr(e,"Invalid card information, please try again");
        }
    }

    // Return the payment amount required. Uses the school's billing start date to determine
    getFirstPaymentAmount() {
        if(this.isFirstPaymentToday()) {
            return parseFloat((this.dataSvc.daycare.fees.cpSetupFee.partialAmount ? this.dataSvc.daycare.fees.cpSetupFee.partialAmount: this.dataSvc.daycare.fees.cpSetupFee.amount).toFixed(2));
        }
        else {
            return 0;
        }
    }

    handleErr(err: any, showAlertText?: string): void {
        console.log(err);
        this.isLoading = false;
        if (showAlertText) {
            this.appSvc.showAlert(showAlertText,"danger-custom", true, 4000);
        }
    }

    getCPSetupFeeDetail(cpSetupFee: any) {
        if(cpSetupFee) {
            let amount: any = cpSetupFee.amount;
            let partialAmount: any = cpSetupFee.partialAmount;
            let startDate: Date = this.dataSvc.getJSDate(cpSetupFee.startDate);
            if (amount && partialAmount && startDate) {
                return Math.floor(parseFloat(amount)/parseFloat(partialAmount)) + " payments of $" + partialAmount + " starting on " + (new Date(startDate) > new Date() ? new Date(startDate).toLocaleDateString() : new Date().toLocaleDateString()) + " for a total of $" + amount;
            }
            else {
                return "$" + this.numberToCurrency(amount);
            }
        }
    }

    getSetupFeeStartDate() {
        let cpSetupFee: any = this.dataSvc.daycare.fees.cpSetupFee;
        let startDate: Date = this.dataSvc.getJSDate(cpSetupFee.startDate);
        return startDate;
    }

    isFirstPaymentToday() {
        let startDate: Date = this.getSetupFeeStartDate();
        let startDateMom = moment(startDate);
        let todayMoment = moment();

        // return true if today or earlier date
        if (startDateMom.format('MM-DD-YYYY') === todayMoment.format('MM-DD-YYYY') || startDateMom.isBefore(todayMoment)) {
            return true;
        }

        // if(new Date(startDate) > new Date()) {
        //     return false;
        // }
        // else {
        //     return true;
        // }

        return false;
    }

    numberToCurrency(num: number) {
        let number = Number(num)
        return (number).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    }

    openTerms() {
        this.modalSvc.show(ContractTermsModalComponent)
    }
}